import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import {
    ArrowDown,
    ArrowUp,
    ChevronLeft,
    ChevronRight,
    Eye,
    EyeOff,
    Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

type SortField =
    | "became_shield_mate_at"
    | "twitch_account_created_at"
    | "lifetime_vp"
    | "lifetime_checkins"
    | "lifetime_raids"
    | "lifetime_watch_time_hours";

type SortOrder = "asc" | "desc";

interface Pagination {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
}

interface ShieldMateDetailItem {
    id: string;
    name: string;
    clan_tag: string;
    clan_color: string | null;
    twitch_account_created_at: string | null;
    lifetime_vp: number;
    lifetime_checkins: number;
    lifetime_raids: number;
    lifetime_watch_time_hours: number;
    became_shield_mate_at: string | null;
}

interface ShieldMateDetailResponse {
    player_id: string;
    player_name: string;
    total_shield_mates: number;
    results: ShieldMateDetailItem[];
    pagination: Pagination;
}

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

export default function ShieldMateDetailPage() {
    const { player_id } = useParams<{ player_id: string }>();
    const [data, setData] = useState<ShieldMateDetailResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState<string | null>(null);
    const [revealedEmails, setRevealedEmails] = useState<
        Record<string, string | null>
    >({});
    const [loadingEmailId, setLoadingEmailId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState<SortField>("became_shield_mate_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

    const fetchDetails = useCallback(
        async (page = 1) => {
            if (!player_id) {
                return;
            }
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    page: String(page),
                    page_size: "25",
                    sort_by: sortBy,
                    order: sortOrder,
                });
                if (search.trim()) {
                    params.set("search", search.trim());
                }
                const response = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/shield-mates/${player_id}?${params.toString()}`,
                );

                if (response.ok) {
                    const payload: ShieldMateDetailResponse =
                        await response.json();
                    setData(payload);
                }
            } catch (error) {
                console.error("Error fetching shield mate details:", error);
            } finally {
                setLoading(false);
            }
        },
        [player_id, search, sortBy, sortOrder],
    );

    useEffect(() => {
        fetchDetails(1);
    }, [fetchDetails]);

    const handleSort = (field: SortField) => {
        setSortBy((prev) => {
            if (prev === field) {
                setSortOrder((order) => (order === "asc" ? "desc" : "asc"));
                return prev;
            }

            setSortOrder("desc");
            return field;
        });
    };

    const renderSortButton = (label: string, field: SortField) => (
        <button
            type="button"
            onClick={() => handleSort(field)}
            className="inline-flex items-center gap-1 text-left hover:text-slate-900 dark:hover:text-slate-100"
        >
            <span>{label}</span>
            {sortBy === field ? (
                sortOrder === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                ) : (
                    <ArrowDown className="h-3.5 w-3.5" />
                )
            ) : null}
        </button>
    );

    const handleRemove = useCallback(
        async (shieldMate: ShieldMateDetailItem) => {
            if (!player_id) {
                return;
            }

            const confirmed = window.confirm(
                `Remove ${shieldMate.name} as a shield mate? This will deduct 5,000 VP from both players in the active season.`,
            );
            if (!confirmed) {
                return;
            }

            setRemovingId(shieldMate.id);
            try {
                const response = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/shield-mates/${player_id}/${shieldMate.id}`,
                    {
                        method: "DELETE",
                    },
                );

                if (response.ok) {
                    const currentPage = data?.pagination.page || 1;
                    await fetchDetails(currentPage);
                }
            } catch (error) {
                console.error("Error removing shield mate:", error);
            } finally {
                setRemovingId(null);
            }
        },
        [data?.pagination.page, fetchDetails, player_id],
    );

    const handleToggleEmail = useCallback(
        async (shieldMate: ShieldMateDetailItem) => {
            if (!player_id) {
                return;
            }

            if (
                Object.prototype.hasOwnProperty.call(
                    revealedEmails,
                    shieldMate.id,
                )
            ) {
                setRevealedEmails((prev) => {
                    const next = { ...prev };
                    delete next[shieldMate.id];
                    return next;
                });
                return;
            }

            setLoadingEmailId(shieldMate.id);
            try {
                const response = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/shield-mates/${player_id}/${shieldMate.id}/email`,
                );
                if (response.ok) {
                    const payload: { email: string | null } =
                        await response.json();
                    setRevealedEmails((prev) => ({
                        ...prev,
                        [shieldMate.id]: payload.email,
                    }));
                }
            } catch (error) {
                console.error("Error fetching shield mate email:", error);
            } finally {
                setLoadingEmailId(null);
            }
        },
        [player_id, revealedEmails],
    );

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="mb-6 flex items-center gap-3">
                <Link
                    to="/longhouse/shield-mates"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                >
                    Back to Shield Mates
                </Link>
            </div>

            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {data?.player_name || "Shield Mate Details"}
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                    Total shield mates: {data?.total_shield_mates ?? 0}
                </p>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 p-4 dark:border-slate-700">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by player name..."
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                    </div>
                ) : !data || data.results.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-slate-600 dark:text-slate-400">
                            No shield mates found.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Player
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Clan
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Became Shield Mate",
                                                "became_shield_mate_at",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Twitch Created",
                                                "twitch_account_created_at",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Lifetime VP",
                                                "lifetime_vp",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Checkins",
                                                "lifetime_checkins",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Raids",
                                                "lifetime_raids",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            {renderSortButton(
                                                "Watch Time (hrs)",
                                                "lifetime_watch_time_hours",
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {data.results.map((item) => (
                                        <tr
                                            key={item.id}
                                            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                                        >
                                            <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">
                                                {item.name}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                <span
                                                    className="rounded px-2 py-1 text-xs font-semibold text-white"
                                                    style={{
                                                        backgroundColor:
                                                            item.clan_color ||
                                                            "#475569",
                                                    }}
                                                >
                                                    {item.clan_tag}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.became_shield_mate_at
                                                    ? new Date(
                                                          item.became_shield_mate_at,
                                                      ).toLocaleString()
                                                    : "-"}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.twitch_account_created_at
                                                    ? new Date(
                                                          item.twitch_account_created_at,
                                                      ).toLocaleDateString()
                                                    : "-"}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.lifetime_vp.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.lifetime_checkins.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.lifetime_raids.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.lifetime_watch_time_hours.toLocaleString()}
                                                h
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() =>
                                                            handleToggleEmail(
                                                                item,
                                                            )
                                                        }
                                                        disabled={
                                                            loadingEmailId ===
                                                            item.id
                                                        }
                                                        className="rounded-md border border-slate-300 p-1.5 text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                                        title={
                                                            Object.prototype.hasOwnProperty.call(
                                                                revealedEmails,
                                                                item.id,
                                                            )
                                                                ? "Hide email"
                                                                : "Reveal email"
                                                        }
                                                    >
                                                        {Object.prototype.hasOwnProperty.call(
                                                            revealedEmails,
                                                            item.id,
                                                        ) ? (
                                                            <EyeOff className="h-4 w-4" />
                                                        ) : (
                                                            <Eye className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                    {Object.prototype.hasOwnProperty.call(
                                                        revealedEmails,
                                                        item.id,
                                                    ) ? (
                                                        <span className="text-sm">
                                                            {revealedEmails[
                                                                item.id
                                                            ] || "No email"}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                                            Hidden
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() =>
                                                        handleRemove(item)
                                                    }
                                                    disabled={
                                                        removingId === item.id
                                                    }
                                                    className={clsx(
                                                        "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-white",
                                                        removingId === item.id
                                                            ? "cursor-not-allowed bg-red-400"
                                                            : "bg-red-600 hover:bg-red-700",
                                                    )}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                    {removingId === item.id
                                                        ? "Removing..."
                                                        : "Remove"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                Page {data.pagination.page} of{" "}
                                {data.pagination.total_pages}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() =>
                                        fetchDetails(data.pagination.page - 1)
                                    }
                                    disabled={
                                        !data.pagination.has_previous_page
                                    }
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() =>
                                        fetchDetails(data.pagination.page + 1)
                                    }
                                    disabled={!data.pagination.has_next_page}
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
